// Library methods
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { HubConnection } from "@microsoft/signalr";

// Components
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import IOLOptions from "./IOLOptions/IOLOptions";
import ProgressiveOptions from "./ProgressiveOptions/ProgressiveOptions";
import TitleWithTooltip from "src/components/shared/text/TitleWithTooltip/TitleWithTooltip";
import SelectDropdown from "src/components/shared/inputs/SelectDropdown/SelectDropdown";
import { StarIcon, StarIconOutline } from "src/assets/icons";
import { LensParameterUpdate } from "../LensSimulation";

// Contexts
import { useLiveUpdateContext } from "src/contexts/live-update/LiveUpdateContext";

// Hooks
import useTokenAccess from "src/hooks/auth/useTokenAccess";
import { useSimulationEnvironmentTypes } from "src/services/clinics/hooks/clinic-lenses/simulation-environments/useSimulationEnvironmentTypes";

// Services
import { deviceUpdateSimParameter } from "src/services/signalR/api";

// Types
import { ExamLensType } from "src/services/exams/types";

// Constants
import { ENABLED_SIM_ENVIRONMENTS, eyeTypeEnums } from "src/constants/enums";

type LensSimulationOptionsType = {
  productType: string;
  connection: HubConnection | null;
  currentLens: ExamLensType | undefined;
  currentSimEnv: string;
  isCurrentLensActionDisabled: boolean;
  isDeviceReady: boolean;
  // IOL only: each eye picks its own lens and settings
  isIndividualEyes: boolean;
  // The lens each eye is viewing, keyed by eyeTypeEnums.LEFT/RIGHT
  eyeLenses: Record<string, ExamLensType | undefined>;
  productLenses: Array<ExamLensType>;
  sendLensUpdate: (
    lensName: string,
    update: LensParameterUpdate
  ) => Promise<void>;
};

const LensSimulationOptions = ({
  productType,
  connection,
  currentLens,
  currentSimEnv,
  isCurrentLensActionDisabled,
  isDeviceReady,
  isIndividualEyes,
  eyeLenses,
  productLenses,
  sendLensUpdate,
}: LensSimulationOptionsType) => {
  const { t } = useTranslation();
  const { device } = useLiveUpdateContext();
  const { getToken } = useTokenAccess();
  const currentLensName = currentLens?.name;

  const [envType, setEnvType] = useState({
    displayName: t(currentSimEnv),
    value: currentSimEnv,
  });

  const {
    data: envData,
    isLoading: isLoadingEnvs,
    isError: isErrorEnvs,
  } = useSimulationEnvironmentTypes();

  const formattedEnvOptions = useMemo(
    () =>
      (envData ?? [])
        ?.filter((data) => ENABLED_SIM_ENVIRONMENTS.includes(data))
        ?.map((data) => ({
          displayName: t(data),
          value: data,
        })),
    [t, envData]
  );

  const hasSimEnvs = useMemo(
    () =>
      formattedEnvOptions &&
      formattedEnvOptions?.length > 0 &&
      !isLoadingEnvs &&
      !isErrorEnvs,
    [formattedEnvOptions, isLoadingEnvs, isErrorEnvs]
  );

  // pass parameters to update on the current lens
  const sendLensParameterUpdate = useCallback(
    async (update: LensParameterUpdate) => {
      if (!currentLensName) return;
      await sendLensUpdate(currentLensName, update);
    },
    [currentLensName, sendLensUpdate]
  );

  // Each eye's lens dropdown: every lens of this type, plus switching the eye's lens off.
  const eyeLensOptions = useMemo(
    () => [
      { displayName: t("No Lens"), value: "" },
      ...productLenses.map((lens) => ({
        displayName: lens.name,
        value: lens.name,
      })),
    ],
    [t, productLenses]
  );

  // Picking a lens puts it on that eye; "No Lens" switches off the lens the
  // eye had. The dropdown then follows what the device reports back.
  const handleSetEyeLens = useCallback(
    (eye: string, displayName: string) => {
      const option = eyeLensOptions.find(
        (candidate) => candidate.displayName === displayName
      );
      if (!option) return;

      const currentEyeLens = eyeLenses[eye];
      if (!option.value) {
        if (currentEyeLens) {
          sendLensUpdate(currentEyeLens.name, {
            currentlyBeingViewed: false,
            eye,
          });
        }
      } else if (option.value !== currentEyeLens?.name) {
        sendLensUpdate(option.value, { currentlyBeingViewed: true, eye });
      }
    },
    [eyeLensOptions, eyeLenses, sendLensUpdate]
  );

  const sendSimParameterUpdate = useCallback(
    async (simulationEnvironmentType: string) => {
      if (!device?.id || !connection) return;

      try {
        const token = await getToken();
        await deviceUpdateSimParameter(
          token,
          device.id,
          { simulationEnvironmentType },
          connection
        );
      } catch (e) {
        console.error(e);
      }
    },
    [device?.id, connection, getToken]
  );

  return (
    <Box display="flex" flexDirection="column" gap="8px">
      {!isIndividualEyes && (
        <Typography sx={{ fontSize: "14px", fontWeight: 600 }}>
          {t("Lens Options")}
        </Typography>
      )}

      {productType === "IOL" && isIndividualEyes ? (
        // Side by side when each eye gets at least 260px, stacked otherwise --
        // sized by the panel itself, which sits half-width next to the video.
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
            gap: "16px",
          }}
        >
          {[eyeTypeEnums.LEFT, eyeTypeEnums.RIGHT].map((eye) => {
            const eyeLens = eyeLenses[eye];
            const preferredVal =
              eye === eyeTypeEnums.LEFT
                ? eyeLens?.preferred?.leftEye
                : eyeLens?.preferred?.rightEye;

            return (
              <Box key={eye} display="flex" flexDirection="column" gap="8px">
                <Typography sx={{ fontSize: "14px", fontWeight: 600 }}>
                  {eye === eyeTypeEnums.LEFT ? t("Left Eye") : t("Right Eye")}
                </Typography>
                <Box display="flex" flexDirection="column" gap="4px">
                  <Typography sx={{ fontSize: "12px" }}>{t("Lens")}</Typography>
                  <Box display="flex" alignItems="center" gap="8px">
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <SelectDropdown
                        id={`live-iol-lens-dropdown-${eye.toLowerCase()}`}
                        disabled={!isDeviceReady}
                        dropdownOptions={eyeLensOptions}
                        value={eyeLens?.name ?? t("No Lens")}
                        handleChange={(event) =>
                          handleSetEyeLens(eye, event.target.value)
                        }
                      />
                    </Box>
                    {/* Favourites this eye's lens for this eye -- the same
                        star the lens cards use for both eyes */}
                    <IconButton
                      aria-label={`favorite-lens-${eye.toLowerCase()}`}
                      disabled={!isDeviceReady || !eyeLens}
                      onClick={() => {
                        if (!eyeLens) return;
                        sendLensUpdate(eyeLens.name, {
                          preferred: !preferredVal,
                          eye,
                        });
                      }}
                      sx={{ p: 0 }}
                    >
                      {preferredVal ? (
                        <StarIcon
                          width="20px"
                          height="20px"
                          style={{ color: "#E2772E" }}
                        />
                      ) : (
                        <StarIconOutline
                          width="20px"
                          height="20px"
                          style={{ color: "#000" }}
                        />
                      )}
                    </IconButton>
                  </Box>
                </Box>
                {eyeLens ? (
                  <IOLOptions
                    key={`${eye}-${eyeLens.name}`}
                    eye={eye}
                    currentLens={eyeLens}
                    isCurrentLensActionDisabled={!isDeviceReady}
                    sendLensParameterUpdate={(update) =>
                      sendLensUpdate(eyeLens.name, update)
                    }
                  />
                ) : (
                  <Typography sx={{ fontSize: "14px" }}>
                    {t("Select a lens for this eye to change its parameters.")}
                  </Typography>
                )}
              </Box>
            );
          })}
        </Box>
      ) : currentLens ? (
        <>
          {productType === "IOL" ? (
            <IOLOptions
              key={currentLensName}
              currentLens={currentLens}
              isCurrentLensActionDisabled={isCurrentLensActionDisabled}
              sendLensParameterUpdate={sendLensParameterUpdate}
            />
          ) : (
            <ProgressiveOptions
              key={currentLensName}
              currentLens={currentLens}
              isCurrentLensActionDisabled={isCurrentLensActionDisabled}
              sendLensParameterUpdate={sendLensParameterUpdate}
            />
          )}
        </>
      ) : (
        <Typography sx={{ fontSize: "14px" }}>
          {t("Select a lens to change its parameters.")}
        </Typography>
      )}

      {hasSimEnvs && (
        <>
          <Typography sx={{ fontSize: "14px", fontWeight: 600 }}>
            {t("Simulation Options")}
          </Typography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "repeat(1, 1fr)",
                sm: "repeat(2, 1fr)",
              },
              gap: "8px",
            }}
          >
            <Box display="flex" flexDirection="column" gap="4px">
              <TitleWithTooltip
                title={t("Simulation Environment")}
                tooltip=""
                titleStyle={{ fontSize: "12px" }}
                tooltipStyle={{ width: "16px", height: "16px" }}
              />
              <SelectDropdown
                id="sim-env-type-dropdown"
                dropdownOptions={formattedEnvOptions}
                value={envType.displayName}
                handleChange={async (event) => {
                  const value = formattedEnvOptions?.find(
                    (option) => option.displayName === event.target.value
                  );

                  if (value) {
                    setEnvType({ ...value });
                    await sendSimParameterUpdate(value.value);
                  }
                }}
              />
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
};

export default LensSimulationOptions;
